'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useTVVideoControls, useIsTVDevice } from '../hooks/useTVNavigation';
import { createApiUrl, getApiUrl, getApiBaseUrl } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { watchTracker } from '../lib/watchTracker';
import { vidsrcTracker } from '../lib/vidsrcTracker';
import { fetchAndConvertSubtitle, getLanguageCode, type SubtitleTrack } from '../lib/subtitleConverter';
import { getImageUrl, IMAGE_SIZES } from '../lib/tmdb';
// Vidstack imports
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import { 
  MediaPlayer, 
  MediaProvider,
  Track
} from '@vidstack/react';
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import type { MediaPlayerElement } from 'vidstack';

interface VideoPlayerProps {
  streamUrl: string;
  movieTitle: string;
  progressCheckUrl?: string;
  uploadingInBackground?: boolean;
  uploadJobId?: string;
  source?: 'filestream' | 'gdrive' | 'vidsrc' | 'aiostreams';
  fileId?: string;
  onBack?: () => void;
  onReady?: () => void; // Callback when video is ready to play
  resumeFrom?: number; // Resume playback from this position (in seconds)
  // For watch tracking
  tmdbId?: number;
  posterPath?: string;
  backdropPath?: string;
  overview?: string;
  releaseDate?: string;
  voteAverage?: number;
}

interface UploadStatus {
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
  gdriveFileId: string | null;
  gdriveStreamUrl: string | null;
  error: string | null;
}




export default function VideoPlayer({ 
  streamUrl, 
  movieTitle, 
  progressCheckUrl, 
  uploadingInBackground,
  uploadJobId,
  source = 'gdrive',
  fileId,
  onBack,
  onReady,
  resumeFrom,
  tmdbId,
  posterPath,
  backdropPath,
  overview,
  releaseDate,
  voteAverage
}: VideoPlayerProps) {
  const playerRef = useRef<any>(null); // Using any for Vidstack player ref compatibility
  // Get the underlying video element for property access
  const getVideoElement = (): HTMLVideoElement | null => {
    if (!playerRef.current) return null;
    return (playerRef.current as any).querySelector?.('video') || null;
  };
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const [currentSource, setCurrentSource] = useState<'filestream' | 'gdrive' | 'vidsrc' | 'aiostreams'>(source);
  const [gdriveUrl, setGdriveUrl] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isFullscreenAttempted, setIsFullscreenAttempted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [subtitleTracks, setSubtitleTracks] = useState<SubtitleTrack[]>([]);
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState(false);
  const [activeSubLang, setActiveSubLang] = useState<string | null>('English');
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  
  // Android TV support
  const isTV = useIsTVDevice();
  // Update useTVVideoControls to work with Vidstack player
  // Create a ref that exposes video-like interface for compatibility
  const videoCompatRef = useRef<HTMLVideoElement | null>(null);
  
  useEffect(() => {
    if (playerRef.current && isTV) {
      // Get underlying video element from Vidstack player
      const playerElement = (playerRef.current as any).root?.querySelector('video');
      if (playerElement) {
        videoCompatRef.current = playerElement;
      }
    }
  }, [playerRef.current, isTV]);
  
  useTVVideoControls(videoCompatRef);

  // REMOVED: Immediate fullscreen attempt on mount - it interferes with autoplay
  // Fullscreen will be triggered in handleVideoPlay after video starts playing
  // This ensures autoplay completes first, then fullscreen happens

  useEffect(() => {
    console.log('🎬 VideoPlayer: streamUrl changed to:', streamUrl);
    console.log('🎬 VideoPlayer: source:', source);
    console.log('🎬 VideoPlayer: uploadJobId:', uploadJobId);
    console.log('🎬 VideoPlayer: playerRef.current:', playerRef.current);
    
    if (playerRef.current && streamUrl) {
      console.log('🎬 VideoPlayer: Loading player with URL:', streamUrl);
      // Wait a bit for MediaPlayer to render
      setTimeout(() => {
        const video = getVideoElement();
        if (video) {
          console.log('🎬 VideoPlayer: Found video element, calling load()');
          video.load();
          // REMOVED: Fullscreen attempt here - let it happen in handleVideoPlay after video starts playing
          // This ensures autoplay is not interrupted by fullscreen request
        } else {
          console.warn('🎬 VideoPlayer: Video element not found yet');
        }
      }, 100);
    } else if (!streamUrl) {
      console.log('🎬 VideoPlayer: No stream URL provided');
    }
  }, [streamUrl]);

  // Load and convert subtitles when tmdbId is available (for all sources)
  useEffect(() => {
    const loadSubtitles = async () => {
      // Load subtitles for any source as long as tmdbId is available
      if (!tmdbId) {
        return;
      }

      setIsLoadingSubtitles(true);
      try {
        // Use backend proxy to avoid CORS issues
        const subtitleApiUrl = createApiUrl(`/api/vidsrc/subtitles/${tmdbId}`);
        console.log('📝 Fetching subtitles from:', subtitleApiUrl);
        
        const response = await fetch(subtitleApiUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch subtitles: ${response.statusText}`);
        }
        
        const data = await response.json();
        if (!data.success || !data.tracks) {
          throw new Error('Invalid subtitle response');
        }
        
        const tracks: SubtitleTrack[] = data.tracks;
        console.log('✅ Loaded subtitle tracks:', tracks.length);
        
        // Convert all SRT files to WebVTT using backend proxy to avoid CORS
        const proxyBaseUrl = getApiBaseUrl();
        const convertedTracks = await Promise.all(
          tracks.map(async (track) => {
            try {
              const blobUrl = await fetchAndConvertSubtitle(track.file, proxyBaseUrl);
              console.log(`✅ Converted subtitle: ${track.label}`);
              return { ...track, blobUrl };
            } catch (error) {
              console.error(`❌ Failed to convert subtitle ${track.label}:`, error);
              return null;
            }
          })
        );
        
        // Filter out failed conversions
        const validTracks = convertedTracks.filter((track) => track !== null) as SubtitleTrack[];
        
        console.log(`✅ Successfully loaded ${validTracks.length} subtitle tracks`);
        setSubtitleTracks(validTracks);
        
      } catch (error) {
        console.error('❌ Error loading subtitles:', error);
        // Don't show error to user - subtitles are optional
        setSubtitleTracks([]);
      } finally {
        setIsLoadingSubtitles(false);
      }
    };

    loadSubtitles();
    
    // Cleanup blob URLs on unmount
    return () => {
      subtitleTracks.forEach(track => {
        if (track.blobUrl) {
          URL.revokeObjectURL(track.blobUrl);
        }
      });
    };
  }, [tmdbId]); // Removed source dependency - subtitles work for all sources

  const switchSubtitle = useCallback((label: string | null) => {
    setActiveSubLang(label);
    setSubMenuOpen(false);
    const player = playerRef.current;
    if (!player) return;
    const tracks = player.textTracks?.toArray?.() || [];
    for (const t of tracks) {
      if (t.kind === 'subtitles' || t.kind === 'captions') {
        t.mode = (label && t.label === label) ? 'showing' : 'disabled';
      }
    }
  }, []);

  // Auto-fullscreen function using Vidstack's API
  const attemptAutoFullscreen = useCallback(() => {
    if (!isFullscreenAttempted && !document.fullscreenElement) {
      setIsFullscreenAttempted(true);
      console.log('🎬 Attempting fullscreen using Vidstack API');
      
      const tryDocumentFullscreen = () => {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen().catch((docErr) => {
            console.log('Document fullscreen failed:', docErr);
          });
        } else if ((document.documentElement as any).webkitRequestFullscreen) {
          (document.documentElement as any).webkitRequestFullscreen();
        } else if ((document.documentElement as any).mozRequestFullScreen) {
          (document.documentElement as any).mozRequestFullScreen();
        } else if ((document.documentElement as any).msRequestFullscreen) {
          (document.documentElement as any).msRequestFullscreen();
        }
      };

      if (playerRef.current) {
        // Use Vidstack's enterFullscreen() method as per documentation
        // https://vidstack.io/docs/player/api/fullscreen/
        const player = playerRef.current as any;
        
        if (typeof player.enterFullscreen === 'function') {
          player.enterFullscreen().catch((err: Error) => {
            console.log('Vidstack enterFullscreen failed:', err);
            // Fallback to document fullscreen
            tryDocumentFullscreen();
          });
        } else {
          tryDocumentFullscreen();
        }
      } else {
        tryDocumentFullscreen();
      }
    }
  }, [isFullscreenAttempted]);

  // Auto-fullscreen when video starts playing
  // This ensures autoplay happens first, then fullscreen
  // Note: Must trigger immediately to maintain user gesture context
  // A delay breaks the user interaction context required by browsers
  const handleVideoPlay = () => {
    console.log('🎬 Video: Playing event fired');
    // Trigger immediately to maintain user gesture context
    // Browsers require fullscreen to be in the same event loop as user interaction
    console.log('🎬 Attempting fullscreen from play handler (immediate)');
    attemptAutoFullscreen();
  };

  // Handle fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Trigger fullscreen on first user interaction when video is playing
  // Browsers require user interaction for fullscreen, so we capture it here
  useEffect(() => {
    if (!streamUrl || isFullscreenAttempted) return;

    const handleUserInteraction = (e: Event) => {
      // Check if video is playing
      const video = getVideoElement();
      if (video && !video.paused && !document.fullscreenElement) {
        // Stop event propagation temporarily to prevent other handlers
        e.stopImmediatePropagation();
        attemptAutoFullscreen();
      }
    };

    // Listen for any user interaction that might allow fullscreen
    // Use capture phase to intercept before other handlers
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { 
        once: true, 
        passive: false,
        capture: true 
      });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction, { capture: true });
      });
    };
  }, [streamUrl, isFullscreenAttempted, attemptAutoFullscreen]);

  // Handle ESC/BACK key in video player
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      // Support both Escape (standard) and Back (Android TV)
      if ((event.key === 'Escape' || event.key === 'Back' || event.key === 'XF86Back') && onBack) {
        event.preventDefault();
        event.stopPropagation();
        
        // First, exit fullscreen if active
        if (document.fullscreenElement) {
          const exitFullscreen = document.exitFullscreen ||
                                (document as any).webkitExitFullscreen ||
                                (document as any).mozCancelFullScreen ||
                                (document as any).msExitFullscreen;
          
          if (exitFullscreen) {
            exitFullscreen.call(document).then(() => {
              // After exiting fullscreen, wait a bit then call onBack
              setTimeout(() => {
                onBack();
              }, 100);
            }).catch(() => {
              // If exit fails, still call onBack
              onBack();
            });
          } else {
            onBack();
          }
        } else {
          // Not in fullscreen, just go back
          onBack();
        }
      }
    };

    window.addEventListener('keydown', handleEscKey, true);
    return () => window.removeEventListener('keydown', handleEscKey, true);
  }, [onBack]);
  
  // Make player focusable for TV
  useEffect(() => {
    if (isTV) {
      const video = getVideoElement();
      if (video) {
        video.setAttribute('tabindex', '0');
        video.setAttribute('aria-label', 'Video player');
        
        // Focus video when it starts playing
        const handlePlay = () => {
          if (video && isTV) {
            video.focus();
          }
        };
        
        video.addEventListener('play', handlePlay);
        return () => {
          if (video) {
            video.removeEventListener('play', handlePlay);
          }
        };
      }
    }
  }, [isTV]);

  // Manual fullscreen toggle
  const toggleFullscreen = async () => {
    const video = getVideoElement();
    if (!video) return;

    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        const requestFullscreen = video.requestFullscreen || 
                                 (video as any).webkitRequestFullscreen ||
                                 (video as any).webkitEnterFullscreen ||
                                 (video as any).mozRequestFullScreen ||
                                 (video as any).msRequestFullscreen;
        
        if (requestFullscreen) {
          await requestFullscreen.call(video);
        }
      } else {
        // Exit fullscreen
        const exitFullscreen = document.exitFullscreen ||
                              (document as any).webkitExitFullscreen ||
                              (document as any).mozCancelFullScreen ||
                              (document as any).msExitFullscreen;
        
        if (exitFullscreen) {
          await exitFullscreen.call(document);
        }
      }
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
    }
  };

  // Single clean upload status polling
  useEffect(() => {
    if (!fileId) return;

    let pollInterval: NodeJS.Timeout | null = null;

    const checkUploadStatus = async () => {
      try {
        const response = await fetch(createApiUrl(`/api/upload-status/${encodeURIComponent(fileId)}`));
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setUploadStatus({
              status: data.status,
              progress: data.progress,
              gdriveFileId: data.gdriveFileId,
              gdriveStreamUrl: data.gdriveStreamUrl,
              error: data.error,
            });

            // Switch to GDrive when upload completes
            if (data.status === 'completed' && data.gdriveStreamUrl) {
              console.log('✅ Upload completed - switching to Google Drive');
              setGdriveUrl(data.gdriveStreamUrl);
              
              if (playerRef.current && (currentSource === 'filestream' || currentSource === 'vidsrc')) {
                handleSwitchToGDrive(data.gdriveStreamUrl);
                
                // Update watch_history to use GDrive
                if (tmdbId && data.gdriveFileId) {
                  const headers = useAuthStore.getState().getAuthHeaders();
                  fetch(getApiUrl('WATCH_HISTORY_UPGRADE_TO_GDRIVE'), {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      tmdb_id: tmdbId,
                      gdrive_stream_url: data.gdriveStreamUrl,
                      gdrive_file_id: data.gdriveFileId
                    })
                  }).catch(err => console.error('Failed to upgrade watch history:', err));
                }
                
                // Update tracker if source was vidsrc
                if (source === 'vidsrc' && data.gdriveFileId && data.gdriveStreamUrl) {
                  vidsrcTracker.updateGDriveInfo(data.gdriveFileId, data.gdriveStreamUrl);
                }
              }
              
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
              return;
            }

            // Stop polling on failure
            if (data.status === 'failed') {
              console.error('❌ Upload failed:', data.error);
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
              return;
            }
          }
        } else if (response.status === 404) {
          // Upload job deleted - switch to GDrive URL directly from cache
          console.log('✅ Upload completed - getting GDrive URL from cache');
          
          try {
            // Direct cache lookup - no Telegram bot interaction needed!
            const gdriveResponse = await fetch(createApiUrl(`/api/stream-drive-oauth/${encodeURIComponent(fileId)}?fromCache=true`));
            
            if (gdriveResponse.ok) {
              const gdriveData = await gdriveResponse.json();
              if (gdriveData.success && gdriveData.streamUrl) {
                console.log('✅ Got GDrive URL from cache - switching seamlessly');
                setGdriveUrl(gdriveData.streamUrl);
                
                if (playerRef.current && (currentSource === 'filestream' || currentSource === 'vidsrc')) {
                  handleSwitchToGDrive(gdriveData.streamUrl);
                  
                  // Update watch_history to use GDrive
                  if (tmdbId && gdriveData.fileId) {
                    const headers = useAuthStore.getState().getAuthHeaders();
                    fetch(getApiUrl('WATCH_HISTORY_UPGRADE_TO_GDRIVE'), {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({
                        tmdb_id: tmdbId,
                        gdrive_stream_url: gdriveData.streamUrl,
                        gdrive_file_id: gdriveData.fileId
                      })
                    }).catch(err => console.error('Failed to upgrade watch history:', err));
                  }
                  
                  // Update tracker if source was vidsrc
                  if (source === 'vidsrc' && gdriveData.fileId && gdriveData.streamUrl) {
                    vidsrcTracker.updateGDriveInfo(gdriveData.fileId, gdriveData.streamUrl);
                  }
                }
              } else {
                console.error('❌ Failed to get GDrive URL from cache:', gdriveData);
              }
            } else {
              console.error('❌ Cache lookup failed with status:', gdriveResponse.status);
            }
          } catch (error) {
            console.error('❌ Error getting GDrive URL from cache:', error);
          }
          
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          return;
        }
      } catch (error) {
        console.error('❌ Error checking upload status:', error);
      }
    };

    // Check immediately
    checkUploadStatus();

    // Poll every 30 seconds
    pollInterval = setInterval(checkUploadStatus, 30000);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [fileId]);

  // Handle resume from position
  useEffect(() => {
    const video = getVideoElement();
    
    if (!video || !resumeFrom || resumeFrom <= 0) {
      return;
    }

    console.log('⏩ Resume from position requested:', resumeFrom, 'seconds');

    const handleLoadedMetadata = () => {
      if (video && resumeFrom > 0 && video.duration && resumeFrom < video.duration) {
        console.log('⏩ Seeking to position:', resumeFrom);
        video.currentTime = resumeFrom;
      }
    };

    // If metadata is already loaded, seek immediately
    if (video.readyState >= 1) {
      handleLoadedMetadata();
    } else {
      // Otherwise, wait for metadata to load
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [resumeFrom, streamUrl]); // Re-run when stream URL changes (for source switching)

  // Watch tracking integration
  useEffect(() => {
    const video = getVideoElement();
    
    console.log('🔍 Watch tracking check:', {
      hasVideo: !!video,
      hasTmdbId: !!tmdbId,
      source,
      fileId,
      movieTitle
    });
    
    // Only track if we have video element, TMDB ID, and source (skip vidfast/vidlink as they use iframes)
    if (!video || !tmdbId || !source) {
      console.log('❌ Watch tracking skipped - conditions not met');
      return;
    }

    console.log('✅ Starting watch tracking for:', movieTitle);

    // Use vidsrcTracker for vidsrc source, watchTracker for others
    if (source === 'vidsrc') {
      vidsrcTracker.startTracking({
        tmdb_id: tmdbId,
        title: movieTitle,
        poster_path: posterPath || null,
        backdrop_path: backdropPath || null,
        overview: overview || null,
        release_date: releaseDate || null,
        vote_average: voteAverage || null,
        source_type: 'vidsrc',
        source_id: tmdbId.toString(), // Use TMDB ID as source_id for vidsrc
        stream_url: streamUrl,
        quality: uploadStatus?.status === 'completed' ? undefined : undefined, // TODO: Get quality from backend response
        gdrive_file_id: uploadStatus?.gdriveFileId || null
      }, video);

      // Cleanup on unmount
      return () => {
        console.log('🛑 Stopping vidsrc watch tracking');
        vidsrcTracker.stopTracking();
      };
    } else {
      // Start tracking for filestream/gdrive
      watchTracker.startTracking({
        tmdb_id: tmdbId,
        title: movieTitle,
        poster_path: posterPath || null,
        backdrop_path: backdropPath || null,
        overview: overview || null,
        release_date: releaseDate || null,
        vote_average: voteAverage || null,
        source_type: source === 'aiostreams' ? 'aiostreams' : source,
        source_id: source === 'aiostreams' ? null : (fileId || null),
        stream_url: streamUrl,
        telegram_file_id: source === 'filestream' ? fileId : null,
        gdrive_file_id: source === 'gdrive' ? fileId : null
      }, video);

      // Cleanup on unmount
      return () => {
        console.log('🛑 Stopping watch tracking');
        watchTracker.stopTracking();
      };
    }
  }, [playerRef.current, tmdbId, source, streamUrl, movieTitle, posterPath, backdropPath, overview, releaseDate, voteAverage, fileId, uploadStatus]);

  // Handle seamless source switching
  const handleSwitchToGDrive = (url?: string) => {
    const targetUrl = url || gdriveUrl;
    const video = getVideoElement();
    if (!targetUrl || !video) {
      return;
    }

    setIsSwitching(true);
    console.log('🔄 Switching to Google Drive stream...');

    // Save current playback position
    const currentTime = video.currentTime;
    const wasPlaying = !video.paused;

    console.log(`📍 Current position: ${currentTime}s, was playing: ${wasPlaying}`);

    // Switch source
    video.src = targetUrl;
    video.load();

    // Restore position and playback state
    const onLoaded = () => {
      if (video) {
        video.currentTime = currentTime;
        if (wasPlaying) {
          video.play().catch((err: Error) => {
            console.error('❌ Error resuming playback:', err);
          });
        }
        video.removeEventListener('loadedmetadata', onLoaded);
      }
      setIsSwitching(false);
      setCurrentSource('gdrive');
      console.log('✅ Successfully switched to Google Drive stream');
    };
    
    video.addEventListener('loadedmetadata', onLoaded);
  };

  const handleVideoError = (e: any) => {
    console.error('❌ Video error:', e);
    const video = getVideoElement();
    if (video) {
      console.error('❌ Video src:', video.src);
    }
    
    // Handle expired FileStream URLs
    if (source === 'filestream') {
      console.error('❌ FileStream URL failed to load. The link may have expired.');
      // Show user-friendly error message
      const shouldRetry = window.confirm(
        'The video failed to load. This FileStream link may have expired.\n\n' +
        'Would you like to search for the movie again?'
      );
      
      if (shouldRetry && onBack) {
        onBack();
      }
    }
  };

  const handleVideoLoadStart = () => {
    console.log('🎬 Video: Load started');
    const video = getVideoElement();
    if (video) {
      console.log('🎬 Video: Source URL:', video.src);
    }
  };

  const handleVideoCanPlay = () => {
    console.log('🎬 Video: Can play');
    // Notify parent that video is ready
    if (onReady) {
      onReady();
    }
    
    // DON'T attempt fullscreen here - wait for play event
    // Fullscreen will be triggered in handleVideoPlay after video actually starts playing
  };

  const handleVideoLoadedData = () => {
    console.log('🎬 Video: Data loaded');
  };

  const handleVideoLoadedMetadata = () => {
    console.log('🎬 Video: Metadata loaded');
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="h-full w-full"
      >
      <div className="h-full w-full">
        <div
          className="h-full w-full bg-black relative"
          style={{ 
            position: 'relative', 
            width: '100%', 
            height: '100%'
          }}
        >
          {streamUrl ? (
            <>
              {/* Custom subtitle picker dropdown */}
              {subMenuOpen && subtitleTracks.length > 0 && (
                <div
                  className="vds-subtitle-picker-overlay"
                  onClick={() => setSubMenuOpen(false)}
                >
                  <div className="vds-subtitle-picker" onClick={(e) => e.stopPropagation()}>
                    <div className="vds-subtitle-picker-header">Subtitles</div>
                    <button
                      className={`vds-subtitle-picker-item ${!activeSubLang ? 'active' : ''}`}
                      onClick={() => switchSubtitle(null)}
                    >
                      Off
                    </button>
                    {subtitleTracks.filter(t => t.blobUrl).map((track) => (
                      <button
                        key={track.label}
                        className={`vds-subtitle-picker-item ${activeSubLang === track.label ? 'active' : ''}`}
                        onClick={() => switchSubtitle(track.label)}
                      >
                        {track.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Movie title overlay — visible when controls are shown */}
              <div className="vds-player-title-overlay">
                <h2>{movieTitle}</h2>
              </div>
              <MediaPlayer
                ref={playerRef}
                src={(() => {
                  // For aiostreams, detect video type from URL or default to video/x-matroska for MKV
                  if (source === 'aiostreams') {
                    const urlLower = streamUrl.toLowerCase()
                    let videoType = 'video/x-matroska' // Default for MKV files
                    
                    if (urlLower.includes('.mp4') || urlLower.includes('mp4')) {
                      videoType = 'video/mp4'
                    } else if (urlLower.includes('.webm') || urlLower.includes('webm')) {
                      videoType = 'video/webm'
                    } else if (urlLower.includes('.m3u8')) {
                      videoType = 'application/vnd.apple.mpegurl'
                    }
                    
                    // Return as object with type for Vidstack
                    return {
                      src: streamUrl,
                      type: videoType
                    }
                  }
                  // For other sources, return as string (Vidstack will auto-detect)
                  return streamUrl
                })()}
                autoPlay
                load="eager"
                playsInline
                viewType="video"
                streamType="on-demand"
                className={`w-full h-full ${isTV ? 'tv-video-player' : ''}`}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  position: 'relative', 
                  display: 'block',
                  backgroundColor: '#000',
                  minHeight: '400px'
                }}
                onError={handleVideoError}
                onLoadStart={handleVideoLoadStart}
                onCanPlay={handleVideoCanPlay}
                onPlay={handleVideoPlay}
                onPlaying={handleVideoPlay}
                onLoadedData={handleVideoLoadedData}
                onLoadedMetadata={handleVideoLoadedMetadata}
              >
                <MediaProvider>
                  {subtitleTracks.map((track, index) => {
                    if (!track.blobUrl) return null;
                    
                    const labelLower = track.label.toLowerCase();
                    const isEnglish = labelLower.includes('english') && 
                                      !labelLower.includes('hi2') && 
                                      !labelLower.includes('hi4') && 
                                      !labelLower.includes('hi6');
                    
                    // Check if this is the first clean English track
                    const isFirstEnglish = isEnglish && 
                      subtitleTracks.slice(0, index).every(t => {
                        const tLabel = t.label.toLowerCase();
                        return !(tLabel.includes('english') && 
                                !tLabel.includes('hi2') && 
                                !tLabel.includes('hi4') && 
                                !tLabel.includes('hi6'));
                      });
                    
                    return (
                      <Track
                        key={`${track.label}-${index}`}
                        kind="subtitles"
                        src={track.blobUrl}
                        language={getLanguageCode(track.label)}
                        label={track.label}
                        default={isFirstEnglish}
                      />
                    );
                  })}
                </MediaProvider>
                <DefaultVideoLayout
                  icons={defaultLayoutIcons}
                  slots={{
                    captionButton: (
                      <button
                        className="vds-subtitle-trigger vds-button"
                        onClick={(e) => { e.stopPropagation(); setSubMenuOpen(prev => !prev); }}
                        aria-label="Subtitles"
                        title="Subtitles"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                          <rect x="1" y="4" width="22" height="16" rx="2" />
                          <path d="M7 12h4M13 12h4M7 16h10" />
                        </svg>
                        {activeSubLang && <span className="vds-subtitle-dot" />}
                      </button>
                    )
                  }}
                />
              </MediaPlayer>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-white">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-red-600" />
                <p className={`${isTV ? 'text-2xl' : 'text-lg'}`}>Preparing stream...</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </motion.div>
    </div>
  );
}
